(() => {
  'use strict';
  if (globalThis.JELivePersonalizeLogo) return;
  const ENDPOINT = 'https://images.jaxengraving.com/functions/v1/website-logo';
  const MAX_FILE = 2 * 1024 * 1024;
  const MAX_RESPONSE = 12 * MAX_FILE;
  const MIME = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/svg+xml': 'svg', 'image/webp': 'webp' };
  const fail = key => new Error(key);
  const aborted = () => new DOMException('Aborted', 'AbortError');

  function normalizeUrl(value) {
    const raw = String(value || '').trim();
    if (!raw || raw.length > 2048 || /\s/.test(raw)) throw fail('website_invalid');
    try {
      const url = new URL(/^[a-z][a-z\d+.-]*:/i.test(raw) ? raw : `https://${raw}`);
      if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.port || !url.hostname.includes('.')) throw fail('website_invalid');
      url.protocol = 'https:';
      url.search = ''; url.hash = '';
      return url.href;
    } catch { throw fail('website_invalid'); }
  }

  function abortable(promise, signal) {
    if (signal.aborted) return Promise.reject(aborted());
    return new Promise((resolve, reject) => {
      const cancel = () => reject(aborted());
      signal.addEventListener('abort', cancel, { once: true });
      promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', cancel));
    });
  }

  function candidateFile(item, name) {
    if (!item || !Object.hasOwn(MIME, item.mimeType) || typeof item.dataUrl !== 'string' || item.dataUrl.length > 3 * MAX_FILE) throw fail('website_lookup_error');
    const prefix = `data:${item.mimeType};base64,`;
    if (!item.dataUrl.startsWith(prefix)) throw fail('website_lookup_error');
    const encoded = item.dataUrl.slice(prefix.length);
    if (!/^[A-Za-z0-9+/]+={0,2}$/.test(encoded)) throw fail('website_lookup_error');
    const bytes = Uint8Array.from(atob(encoded), c => c.charCodeAt(0));
    if (!bytes.length || bytes.length > MAX_FILE) throw fail('website_lookup_error');
    const png = bytes[0] === 137 && bytes[1] === 80 && bytes[2] === 78 && bytes[3] === 71;
    const jpeg = bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255;
    const webp = new TextDecoder().decode(bytes.slice(0, 4)) === 'RIFF' && new TextDecoder().decode(bytes.slice(8, 12)) === 'WEBP';
    if (item.mimeType !== 'image/svg+xml' && !({ 'image/png': png, 'image/jpeg': jpeg, 'image/webp': webp })[item.mimeType]) throw fail('website_lookup_error');
    return new File([bytes], name, { type: item.mimeType });
  }

  async function raster(file, signal) {
    const url = URL.createObjectURL(file), image = new Image();
    try {
      await abortable(new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = () => reject(fail('website_lookup_error'));
        image.src = url;
      }), signal);
      return image;
    } catch (error) { image.src = ''; throw error; }
    finally { image.onload = null; image.onerror = null; URL.revokeObjectURL(url); }
  }

  async function prepareCandidate(item, index, host, signal) {
    let mime = item.mimeType;
    let name = `${host}-logo-${index + 1}.${MIME[mime] || 'png'}`;
    const file = candidateFile(item, name);
    let source;
    if (mime === 'image/svg+xml') {
      if (!globalThis.EngravingFileImport) throw fail('website_lookup_error');
      source = (await abortable(globalThis.EngravingFileImport.render(file), signal)).source;
    } else source = await raster(file, signal);
    if (signal.aborted) throw aborted();
    if (!source?.naturalWidth || !source.naturalHeight || source.naturalWidth * source.naturalHeight > 20000000) throw fail('website_lookup_error');
    const canvas = document.createElement('canvas');
    const ratio = Math.min(1, 1600 / Math.max(source.naturalWidth, source.naturalHeight));
    canvas.width = Math.max(1, Math.round(source.naturalWidth * ratio));
    canvas.height = Math.max(1, Math.round(source.naturalHeight * ratio));
    canvas.getContext('2d').drawImage(source, 0, 0, canvas.width, canvas.height);
    let original = item.dataUrl;
    if (mime === 'image/webp') {
      original = canvas.toDataURL('image/png'); mime = 'image/png'; name = name.replace(/\.webp$/, '.png');
      if (original.length > 14000000) throw fail('website_lookup_error');
    }
    const thumbnail = document.createElement('canvas');
    const thumbRatio = Math.min(1, 320 / Math.max(canvas.width, canvas.height));
    thumbnail.width = Math.max(1, Math.round(canvas.width * thumbRatio));
    thumbnail.height = Math.max(1, Math.round(canvas.height * thumbRatio));
    thumbnail.getContext('2d').drawImage(canvas, 0, 0, thumbnail.width, thumbnail.height);
    return {
      asset: { id: crypto.randomUUID(), name, mime, original },
      thumbnail: thumbnail.toDataURL('image/png'),
      label: typeof item.label === 'string' ? item.label.replace(/[\u0000-\u001f\u007f]/g, '').slice(0, 140) : '',
      source: typeof item.source === 'string' ? item.source.slice(0, 80) : ''
    };
  }

  async function lookup(value, { signal } = {}) {
    const url = normalizeUrl(value);
    if (signal?.aborted) throw aborted();
    const request = new AbortController();
    const cancel = () => request.abort();
    signal?.addEventListener('abort', cancel, { once: true });
    let timedOut = false;
    const timer = setTimeout(() => { timedOut = true; request.abort(); }, 20000);
    try {
      const response = await fetch(ENDPOINT, {
        method: 'POST', credentials: 'omit', redirect: 'error', signal: request.signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ url })
      });
      if (response.status === 429) throw fail('website_rate_limit');
      if (!response.ok || !response.headers.get('content-type')?.includes('application/json') || Number(response.headers.get('content-length')) > MAX_RESPONSE) throw fail('website_lookup_error');
      const text = await response.text();
      if (text.length > MAX_RESPONSE) throw fail('website_lookup_error');
      const body = JSON.parse(text);
      if (body.ok !== true || !Array.isArray(body.data?.logos)) throw fail('website_lookup_error');
      const logos = []; let skipped = 0;
      const host = new URL(url).hostname.replace(/^www\./, '').replace(/[^a-z\d.-]/gi, '_').slice(0, 90);
      for (const [index, item] of body.data.logos.slice(0, 6).entries()) {
        if (request.signal.aborted) throw aborted();
        try { logos.push(await prepareCandidate(item, index, host, request.signal)); }
        catch (error) { if (request.signal.aborted) throw error; skipped++; }
      }
      if (request.signal.aborted) throw aborted();
      return { logos, skipped };
    } catch (error) {
      if (signal?.aborted) throw aborted();
      if (timedOut) throw fail('website_timeout');
      if (error?.message === 'website_rate_limit') throw error;
      throw fail('website_lookup_error');
    } finally { clearTimeout(timer); signal?.removeEventListener('abort', cancel); request.abort(); }
  }

  globalThis.JELivePersonalizeLogo = Object.freeze({ normalizeUrl, lookup });
})();
